'use client';

import { FC, ReactNode, useEffect } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import { usePostHog } from 'posthog-js/react';

interface ProductHeroProps {
  icon?: ReactNode;
  iconSrc?: string;
  label: string;
  title: string;
  highlightedWord: string;
  subtitle: string;
  primaryCTA: {
    label: string;
    href: string;
  };
  secondaryCTA?: {
    label: string;
    href: string;
  };
  backgroundImage?: string;
  videoUrl?: string;
}

const ProductHero: FC<ProductHeroProps> = ({
  icon,
  iconSrc,
  label,
  title,
  highlightedWord,
  subtitle,
  primaryCTA,
  secondaryCTA,
  backgroundImage = '/images/products/hero-background.svg',
  videoUrl,
}) => {
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture('product_page_viewed', { product_name: label });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Split title to insert highlighted word with blue color
  const renderTitle = () => {
    const parts = title.split(highlightedWord);
    if (parts.length === 1) {
      return <>{title}</>;
    }
    return (
      <>
        {parts[0]}
        <span style={{ color: '#094FFE' }}>{highlightedWord}</span>
        {parts[1]}
      </>
    );
  };

  return (
    <section className="pt-4 pb-8 md:pt-8 md:pb-12">
      <div className="container-fluid px-4 md:px-7.5 mx-auto" style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}>
        {/* Hero Container with Background */}
        <div
          className="pt-6 pb-8 px-5 sm:pt-10 sm:pb-10 sm:px-10 tablet:pt-16 tablet:px-20 tablet:pb-16 bg-white rounded-[20px] relative overflow-hidden"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <FadeIn variant="down" delay={0.1}>
            {/* Icon and Label */}
            <div className="flex flex-col items-center mb-6">
              {iconSrc ? (
                <Image
                  src={iconSrc}
                  alt={label}
                  width={48}
                  height={48}
                  className="mb-3"
                />
              ) : icon ? (
                <div className="w-12 h-12 mb-3 flex items-center justify-center text-slate-600">
                  {icon}
                </div>
              ) : null}
              <span className="text-sm text-slate-500 uppercase tracking-wider font-medium">
                {label}
              </span>
            </div>

            {/* Title - matching homepage hero */}
            <h1
              className="text-center font-medium text-black mb-6"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 3rem)',
                lineHeight: 'clamp(32px, 4vw, 60px)',
              }}
            >
              {renderTitle()}
            </h1>

            {/* Subtitle - matching homepage hero */}
            <p
              className="text-gray-700 text-center mx-auto mb-10"
              style={{
                fontSize: 'clamp(0.875rem, 1.25vw, 1.5rem)',
                maxWidth: 'clamp(300px, 60vw, 800px)',
              }}
            >
              {subtitle}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {secondaryCTA && (
                <a
                  href={secondaryCTA.href}
                  className="px-6 py-3 text-slate-700 border border-slate-300 rounded-xl font-medium hover:bg-slate-50 transition-colors"
                  style={{ fontSize: 'clamp(0.9375rem, 1.5vw, 1.5rem)' }}
                  onClick={() => posthog?.capture('product_cta_clicked', { product_name: label, cta_position: 'hero_secondary' })}
                >
                  {secondaryCTA.label}
                </a>
              )}
              <a
                href={primaryCTA.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => posthog?.capture('product_cta_clicked', { product_name: label, cta_position: 'hero' })}
                className="group relative font-normal text-white rounded-xl transition-all duration-300 overflow-hidden shadow-lg hover:shadow-xl inline-block"
                style={{
                  backgroundColor: '#0848E7',
                  minWidth: '180px',
                  padding: 'clamp(0.625rem, 1vw, 1rem) clamp(2rem, 3.5vw, 3.5rem)',
                  fontSize: 'clamp(0.9375rem, 1.5vw, 1.5rem)'
                }}
                onMouseEnter={(e) => {
                  const button = e.currentTarget;
                  button.style.background = 'linear-gradient(to right, #3EA67E, #084FFE)';
                }}
                onMouseLeave={(e) => {
                  const button = e.currentTarget;
                  button.style.background = '#0848E7';
                }}
              >
                {primaryCTA.label}
              </a>
            </div>

            {/* Temporarily hidden - Video section until videos are ready */}
            {/* <div className="mt-12 md:mt-16 px-4 sm:px-6 tablet:px-10">
              <div
                className="bg-slate-100 rounded-2xl overflow-hidden relative shadow-lg border border-gray-100"
                style={{ aspectRatio: '16/9' }}
              >
                {videoUrl ? (
                  <video
                    src={videoUrl}
                    controls
                    className="w-full h-full object-cover"
                    poster={`${videoUrl.replace('.mp4', '_thumb.jpg')}`}
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-slate-400 text-center">
                      <svg
                        className="w-20 h-20 mx-auto mb-3 opacity-50"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      <span className="text-base">Video coming soon</span>
                    </div>
                  </div>
                )}
              </div>
            </div> */}
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default ProductHero;
